import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiHandle from "services/apiHandle";

// ==================== Async Thunks ====================

// Create a new dispute
export const createDispute = createAsyncThunk(
  "disputes/createDispute",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/disputes/creates", "POST", body);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to create dispute";
      return rejectWithValue(message);
    }
  }
);

// Submit dispute evidence (with file upload)
export const submitDisputeEvidence = createAsyncThunk(
  "disputes/submitDisputeEvidence",
  async ({ disputeId, formData }, { rejectWithValue }) => {
    try {
      const response = await apiHandle(
        `/disputes/${disputeId}/evidence`,
        "POST",
        formData,
        { "Content-Type": "multipart/form-data" }
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to submit evidence";
      return rejectWithValue(message);
    }
  }
);

// Get my disputes (with optional query params)
export const getMyDisputes = createAsyncThunk(
  "disputes/getMyDisputes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/disputes/my-disputes?${queryString}`
        : "/disputes/my-disputes";
      const response = await apiHandle(endpoint, "GET");
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch disputes";
      return rejectWithValue(message);
    }
  }
);

// Get dispute by ID
export const getDisputeById = createAsyncThunk(
  "disputes/getDisputeById",
  async (disputeId, { rejectWithValue }) => {
    try {
      const response = await apiHandle(`/disputes/${disputeId}`, "GET");
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch dispute details";
      return rejectWithValue(message);
    }
  }
);

// ==================== Slice ====================

const disputesSlice = createSlice({
  name: "disputes",
  initialState: {
    disputes: [],
    currentDispute: null,
    loading: false,
    detailLoading: false,
    createLoading: false,
    evidenceLoading: false,
    error: null,
    message: null,
    showMessage: false,
  },
  reducers: {
    clearDisputeMessage: (state) => {
      state.message = null;
      state.showMessage = false;
      state.error = null;
    },
    setCurrentDispute: (state, action) => {
      state.currentDispute = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Dispute
      .addCase(createDispute.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createDispute.fulfilled, (state, action) => {
        state.createLoading = false;
        state.message = "Dispute created successfully";
        state.showMessage = true;
        if (action.payload?.dispute) {
          state.disputes.unshift(action.payload.dispute);
        }
      })
      .addCase(createDispute.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Submit Evidence
      .addCase(submitDisputeEvidence.pending, (state) => {
        state.evidenceLoading = true;
        state.error = null;
      })
      .addCase(submitDisputeEvidence.fulfilled, (state, action) => {
        state.evidenceLoading = false;
        state.message = "Evidence submitted successfully";
        state.showMessage = true;
        // Update the dispute in the list if it exists
        if (action.payload?.dispute) {
          const index = state.disputes.findIndex(
            (d) => d._id === action.payload.dispute._id
          );
          if (index !== -1) {
            state.disputes[index] = action.payload.dispute;
          }
          if (state.currentDispute?._id === action.payload.dispute._id) {
            state.currentDispute = action.payload.dispute;
          }
        }
      })
      .addCase(submitDisputeEvidence.rejected, (state, action) => {
        state.evidenceLoading = false;
        state.error = action.payload;
        state.showMessage = true;
      })

      // Get My Disputes
      .addCase(getMyDisputes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyDisputes.fulfilled, (state, action) => {
        state.loading = false;
        state.disputes = action.payload?.disputes || action.payload || [];
      })
      .addCase(getMyDisputes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Dispute By ID
      .addCase(getDisputeById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getDisputeById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentDispute = action.payload?.dispute || action.payload;
      })
      .addCase(getDisputeById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDisputeMessage, setCurrentDispute } =
  disputesSlice.actions;
export default disputesSlice.reducer;
